import {ApolloQueryResult, gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import FieldActionButtons from 'metadata/FieldActionButtons';
import notifications from 'notifications';
import {
  MAX_PARAMETER_DESCRIPTION_LENGTH,
  MAX_PARAMETER_NAME_LENGTH,
} from 'procedures/design/ProcedureDesignConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {ButtonSize} from 'ui-library/buttons/Button';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldError from 'ui-library/FieldError';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useDraftState from 'ui-library/hooks/useDraftState';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';
import SearchableUnitTypeDropdown from 'units/SearchableUnitTypeDropdown';
import strUtil from 'util/strUtil';
import {InputChangeEvent} from 'util/Types';

import {createLibraryParameterDefinition} from '../network/LibraryParameterDefinitionDAO';
import {
  isLibraryParameterDefinitionDataType,
  LibraryParameterDefinition,
  LibraryParameterDefinitionDataType,
} from '../ParameterLibraryTypes';
import {
  ParameterLibraryListingQuery,
  ParameterLibraryListingQueryVariables,
} from './__generated/ParameterLibraryListingQuery';
import {LibraryParameterDefinitionDataTypeSelector} from './LibraryParameterDefinitionDataTypeSelector';

const useStyles = createStyles(() => ({
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  archivedField: {
    opacity: 0.6,
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginTop: '6px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  search: {
    width: 300,
    verticalAlign: 'middle',
    marginRight: '10px',
  },
  filteringControls: {
    display: 'flex',
  },
  addButton: {
    marginLeft: '10px',
  },
  auditAndAddButtons: {
    display: 'flex',
  },
  descriptionCell: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
  },
}));

/** @package */
export const LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME: Record<
  LibraryParameterDefinitionDataType,
  string
> = {
  [FieldDefinitionType.FLOAT]: 'Decimal',
  [FieldDefinitionType.TEXT]: 'Text',
  [FieldDefinitionType.DATETIME]: 'Datetime',
};

export const PARAMETER_LIBRARY_LISTING_QUERY = gql`
  query ParameterLibraryListingQuery @skipAudit @engComponent(name: PROCEDURES) {
    allLibraryParameterDefinitions {
      ... on LibraryParameterDefinitionNode {
        id
        name
        dataType
        description
        archiveData {
          archived
          purpose
        }
        unitType {
          ... on UnitTypeNode {
            id
            name
          }
        }
      }
    }
    currentTenant {
      id
      permissions {
        canCreateLibraryParameterDefinitions
      }
    }
  }
`;

interface DraftLibraryParameterDefinition {
  name: string;
  dataType: LibraryParameterDefinitionDataType;
  description: string;
  unitTypeId: string | null;
}

const EMPTY_PARAMETER_DEFINITION: DraftLibraryParameterDefinition = {
  name: '',
  dataType: FieldDefinitionType.FLOAT,
  description: '',
  unitTypeId: null,
};

export default function ParameterLibraryListing(): JSX.Element {
  const queryResult = useBenchlingQuery<ParameterLibraryListingQuery, ParameterLibraryListingQueryVariables>(
    PARAMETER_LIBRARY_LISTING_QUERY,
    {renderCachedDataWhenRefetching: true}
  );

  useEffect(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      queryResult.data.allLibraryParameterDefinitions.forEach((parameterDefinition) => {
        assert(
          isLibraryParameterDefinitionDataType(parameterDefinition.dataType),
          `Unexpected data type for library parameter definition: ${parameterDefinition.dataType}`
        );
      });
    }
  }, [queryResult]);

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return <ErrorMessage error="Failed to load parameter library" />;
  }
  return (
    <ParameterLibraryListingDisplay
      parameterDefinitions={
        queryResult.data.allLibraryParameterDefinitions as unknown as Array<LibraryParameterDefinition>
      }
      refetch={queryResult.refetch}
      canCreateLibraryParameterDefinitions={
        queryResult.data.currentTenant.permissions.canCreateLibraryParameterDefinitions
      }
    />
  );
}

/**
 * Presentational component for parameter library listing.
 * @package
 */
export function ParameterLibraryListingDisplay({
  parameterDefinitions,
  canCreateLibraryParameterDefinitions,
  refetch,
}: {
  parameterDefinitions: Array<LibraryParameterDefinition>;
  canCreateLibraryParameterDefinitions: boolean;
  refetch: () => Promise<ApolloQueryResult<ParameterLibraryListingQuery>>;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArchivePurposes, selectArchivePurposes] = useState<Array<string>>([
    NOT_ARCHIVED_PURPOSE_STRING,
  ]);
  // Used to manage state for the row being created or updated
  const draftState = useDraftState<DraftLibraryParameterDefinition>(EMPTY_PARAMETER_DEFINITION);
  const {
    data: draftParameterDefinition,
    updateDraftData: updateDraftParameterDefinition,
    isEditing,
    startEditing,
    stopEditing,
  } = draftState;

  const {classes} = useStyles();
  const trimmedSearchQuery = searchQuery.trim().toLowerCase();
  const isSearching = !strUtil.isEmpty(trimmedSearchQuery);

  const allParameterDefinitionsIds = parameterDefinitions.map((pd) => pd.id);

  const sortedParameterDefinitions = naturalSortByKey(parameterDefinitions, 'name');
  const parameterDefinitionsMatchingQuery = isSearching
    ? sortedParameterDefinitions.filter((pd) => pd.name.toLowerCase().includes(trimmedSearchQuery))
    : sortedParameterDefinitions;
  const filteredParameterDefinitions = parameterDefinitionsMatchingQuery.filter((pd) =>
    pd.archiveData.archived
      ? selectedArchivePurposes.includes(pd.archiveData.purpose!)
      : selectedArchivePurposes.includes(NOT_ARCHIVED_PURPOSE_STRING)
  );

  function getArchivedClass(rowIndex: number): string | undefined {
    return rowIndex < filteredParameterDefinitions.length &&
      filteredParameterDefinitions[rowIndex].archiveData.archived
      ? classes.archivedField
      : undefined;
  }

  function isNewParameterDefinitionRow(rowIndex: number): boolean {
    return rowIndex === filteredParameterDefinitions.length;
  }

  function onArchive(parameterDefinition: LibraryParameterDefinition): void {
    ArchiveModal.open({
      items: [parameterDefinition],
      onArchive: async (itemIds: Array<string>, purpose: string): Promise<void> => {
        await coreObjectArchiveAction(itemIds, purpose);
      },
    });
  }

  async function onUnarchive(parameterDefinition: LibraryParameterDefinition): Promise<void> {
    const unarchiveFn = getUnarchiveAction([parameterDefinition.id]);
    await unarchiveFn([parameterDefinition.id]);
  }

  function onCancel(): void {
    stopEditing();
    updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
  }

  const nameExceedsMaxLength = draftParameterDefinition.name.length > MAX_PARAMETER_NAME_LENGTH;
  const descriptionExceedsMaxLength =
    draftParameterDefinition.description.length > MAX_PARAMETER_DESCRIPTION_LENGTH;
  const isValid =
    !strUtil.isEmpty(draftParameterDefinition.name) && !nameExceedsMaxLength && !descriptionExceedsMaxLength;

  async function onAddParameterDefinition(): Promise<void> {
    await createLibraryParameterDefinition(draftParameterDefinition);
    stopEditing();
    await refetch();
    updateDraftParameterDefinition(EMPTY_PARAMETER_DEFINITION);
    notifications.success(
      <>
        Parameter <strong>{draftParameterDefinition.name}</strong> added
      </>
    );
  }

  const nameErrorMessage = nameExceedsMaxLength ? 'Name is too long' : null;
  const descriptionErrorMessage = descriptionExceedsMaxLength ? 'Description is too long' : null;

  function renderNameCell(rowIndex: number): JSX.Element {
    return isNewParameterDefinitionRow(rowIndex) ? (
      <Cell>
        <FormInput
          type="text"
          value={draftParameterDefinition.name}
          onChange={(e) => {
            updateDraftParameterDefinition({name: e.target.value});
          }}
          error={nameErrorMessage ?? undefined}
        />
        <FieldError message={nameErrorMessage} compact={true} />
      </Cell>
    ) : (
      <Cell className={getArchivedClass(rowIndex)}>
        {filteredParameterDefinitions[rowIndex].name}
        {filteredParameterDefinitions[rowIndex].archiveData.archived
          ? ` (${filteredParameterDefinitions[rowIndex].archiveData.purpose})`
          : ''}
      </Cell>
    );
  }

  function renderDataTypeCell(rowIndex: number): JSX.Element {
    return isNewParameterDefinitionRow(rowIndex) ? (
      <Cell>
        <LibraryParameterDefinitionDataTypeSelector
          selectedType={draftParameterDefinition.dataType}
          onChangeSelection={(newType) => {
            updateDraftParameterDefinition({
              dataType: newType,
              // Clear unit type if switching to text
              unitTypeId: newType === FieldDefinitionType.TEXT ? null : draftParameterDefinition.unitTypeId,
            });
          }}
        />
      </Cell>
    ) : (
      <Cell className={getArchivedClass(rowIndex)}>
        {
          LIBRARY_PARAMETER_DEFINITION_DATA_TYPE_TO_FRIENDLY_NAME[
            filteredParameterDefinitions[rowIndex].dataType
          ]
        }
      </Cell>
    );
  }

  function renderUnitTypeCell(rowIndex: number): JSX.Element {
    return isNewParameterDefinitionRow(rowIndex) ? (
      <Cell>
        <SearchableUnitTypeDropdown
          selectedId={draftParameterDefinition.unitTypeId}
          onSelect={(selectedUnitType) => {
            updateDraftParameterDefinition({
              unitTypeId: selectedUnitType?.id ?? null,
            });
          }}
          includeNone={true}
          disabled={draftParameterDefinition.dataType !== FieldDefinitionType.FLOAT}
        />
      </Cell>
    ) : (
      <Cell className={getArchivedClass(rowIndex)}>
        {filteredParameterDefinitions[rowIndex].unitType?.name ?? ''}
      </Cell>
    );
  }

  function renderDescriptionCell(rowIndex: number): JSX.Element {
    const descriptionContent = isNewParameterDefinitionRow(rowIndex) ? (
      <>
        <FormInput
          type="text"
          value={draftParameterDefinition.description}
          onChange={(e) => {
            updateDraftParameterDefinition({description: e.target.value});
          }}
          error={descriptionErrorMessage ?? undefined}
        />
        <FieldError message={descriptionErrorMessage} compact={true} />
      </>
    ) : (
      filteredParameterDefinitions[rowIndex].description
    );
    return (
      <Cell className={getArchivedClass(rowIndex)}>
        <div className={classes.descriptionCell}>{descriptionContent}</div>
      </Cell>
    );
  }

  function renderFieldActionButtonsCell(rowIndex: number): JSX.Element {
    const actionButtons = isNewParameterDefinitionRow(rowIndex) ? (
      <FieldActionButtons
        className={classes.actionButtons}
        isEditing={true}
        unarchive={undefined}
        archive={undefined}
        cancel={onCancel}
        complete={onAddParameterDefinition}
        isCancelEnabled={isEditing}
        // TODO (BNCH-133682): Add uniquness validation
        isCompleteEnabled={isEditing && isValid}
      />
    ) : (
      <FieldActionButtons
        className={classes.actionButtons}
        isEditing={false}
        unarchive={
          filteredParameterDefinitions[rowIndex].archiveData.archived
            ? () => {
                onUnarchive(filteredParameterDefinitions[rowIndex]);
              }
            : undefined
        }
        isUnarchiveEnabled={!isEditing}
        archive={
          !filteredParameterDefinitions[rowIndex].archiveData.archived
            ? () => {
                onArchive(filteredParameterDefinitions[rowIndex]);
              }
            : undefined
        }
        isArchiveEnabled={!isEditing}
      />
    );
    return <Cell>{actionButtons}</Cell>;
  }

  return (
    <div {...getTestProps({component: 'ParameterLibraryListing', element: 'root'})}>
      <div className={classes.tableControls}>
        <div className={classes.filteringControls}>
          <PlainSearchInput
            className={classes.search}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery('');
            }}
            placeholder="Search"
            value={searchQuery}
          />
          <ArchivePurposeDropdown
            itemIds={allParameterDefinitionsIds}
            selectedPurposes={selectedArchivePurposes}
            onChangePurposes={(newPurposes) => {
              selectArchivePurposes(newPurposes);
            }}
          />
        </div>
        <div className={classes.auditAndAddButtons}>
          <Tooltip text={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
            <Button
              icon="audit-trail"
              onClick={() => {
                exportAuditLogs(allParameterDefinitionsIds);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          </Tooltip>
          {canCreateLibraryParameterDefinitions && (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text="Add"
              icon="compose"
              className={classes.addButton}
              onClick={() => {
                startEditing();
              }}
              disabled={isEditing}
            />
          )}
        </div>
      </div>
      <Table
        frozenHeader={true}
        overflowVisible={true}
        showBorder={false}
        // TODO (BNCH-133006): Check if we're creating vs updating here
        numRows={isEditing ? filteredParameterDefinitions.length + 1 : filteredParameterDefinitions.length}
      >
        <Column header="Name" renderCell={renderNameCell} />
        <Column header="Data type" renderCell={renderDataTypeCell} />
        <Column header="Unit type" renderCell={renderUnitTypeCell} />
        <Column header="Description" renderCell={renderDescriptionCell} />
        <Column
          // no header for action buttons
          header=""
          renderCell={renderFieldActionButtonsCell}
        />
      </Table>
    </div>
  );
}
